import React from 'react'
import { Link } from 'react-router-dom'

const STORAGE_KEY = 'mf_cookie_consent'

export type ConsentChoice = 'all' | 'essential'

export function getConsentChoice(): ConsentChoice | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed.choice ?? null
  } catch {
    return null
  }
}

function saveConsent(choice: ConsentChoice) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ choice, timestamp: Date.now() }))
}

export default function CookieConsent() {
  const [visible, setVisible] = React.useState(false)

  React.useEffect(() => {
    if (!getConsentChoice()) {
      // Small delay so the banner doesn't flash on the very first paint
      const t = setTimeout(() => setVisible(true), 600)
      return () => clearTimeout(t)
    }
  }, [])

  function accept(choice: ConsentChoice) {
    saveConsent(choice)
    setVisible(false)
  }

  if (!visible) return null

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-0 inset-x-0 z-50 p-4 sm:p-6 flex justify-center"
    >
      <div className="w-full max-w-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl p-5 sm:p-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
            We use cookies / Nous utilisons des témoins
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
            MortgageFlow uses essential cookies to operate and optional cookies to improve your
            experience. Under Quebec Law 25 (PPIPSA) and PIPEDA you can choose which cookies
            you allow.{' '}
            <Link to="/privacy" className="underline hover:text-gray-900 dark:hover:text-white">
              Privacy policy
            </Link>
          </p>
        </div>

        {/* Buttons */}
        <div className="flex gap-2 flex-shrink-0 w-full sm:w-auto">
          <button
            onClick={() => accept('essential')}
            className="flex-1 sm:flex-none px-4 py-2 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            Essential only
          </button>
          <button
            onClick={() => accept('all')}
            className="flex-1 sm:flex-none px-4 py-2 text-xs font-semibold rounded-lg bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-700 dark:hover:bg-gray-100 transition-colors"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  )
}
